import { Component, EventEmitter, inject, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { SearchForm } from '../../../../shared/interfaces/search-filter';
import { AdvancedFiltersBuilder } from '../../models/advanced-filters';
import { ClickOutsideDirective } from '../../../../shared/directives/click-outside/click-outside.directive';
import { TourService } from '../../../../shared/services/tour-service/tour.service';
import { MultiselectFilterComponent } from '../../../../shared/components/multiselect-filter/multiselect-filter.component';

interface State {
  value: string;
  name: string;
}

@Component({
  selector: 'app-search-data-axle',
  standalone: true,
  imports: [FormsModule, ClickOutsideDirective, MultiselectFilterComponent],
  templateUrl: './search-data-axle.component.html',
  styleUrl: './search-data-axle.component.scss',
})
export class SearchDataAxleComponent {
  tourService = inject(TourService);

  @Output() searchSubmitted = new EventEmitter<AdvancedFiltersBuilder>();
  @Output() filterCountChanged = new EventEmitter<number>();
  @Output() reloadAllData = new EventEmitter<void>();

  searchForm: SearchForm = {};
  isFormValid = false;
  isAdvancedSearchVisible = true;
  selectedStates: string[] = [];
  matchType: 'all' | 'any' = 'all';
  isSearchTypeDropdownOpen = false;
  filterCount = 0;

  states: State[] = [
    { value: 'AL', name: 'Alabama' },
    { value: 'AK', name: 'Alaska' },
    { value: 'AZ', name: 'Arizona' },
    { value: 'AR', name: 'Arkansas' },
    { value: 'CA', name: 'California' },
    { value: 'CO', name: 'Colorado' },
    { value: 'CT', name: 'Connecticut' },
    { value: 'DE', name: 'Delaware' },
    { value: 'DC', name: 'District of Columbia' },
    { value: 'FL', name: 'Florida' },
    { value: 'GA', name: 'Georgia' },
    { value: 'HI', name: 'Hawaii' },
    { value: 'ID', name: 'Idaho' },
    { value: 'IL', name: 'Illinois' },
    { value: 'IN', name: 'Indiana' },
    { value: 'IA', name: 'Iowa' },
    { value: 'KS', name: 'Kansas' },
    { value: 'KY', name: 'Kentucky' },
    { value: 'LA', name: 'Louisiana' },
    { value: 'ME', name: 'Maine' },
    { value: 'MD', name: 'Maryland' },
    { value: 'MA', name: 'Massachusetts' },
    { value: 'MI', name: 'Michigan' },
    { value: 'MN', name: 'Minnesota' },
    { value: 'MS', name: 'Mississippi' },
    { value: 'MO', name: 'Missouri' },
    { value: 'MT', name: 'Montana' },
    { value: 'NE', name: 'Nebraska' },
    { value: 'NV', name: 'Nevada' },
    { value: 'NH', name: 'New Hampshire' },
    { value: 'NJ', name: 'New Jersey' },
    { value: 'NM', name: 'New Mexico' },
    { value: 'NY', name: 'New York' },
    { value: 'NC', name: 'North Carolina' },
    { value: 'ND', name: 'North Dakota' },
    { value: 'OH', name: 'Ohio' },
    { value: 'OK', name: 'Oklahoma' },
    { value: 'OR', name: 'Oregon' },
    { value: 'PA', name: 'Pennsylvania' },
    { value: 'RI', name: 'Rhode Island' },
    { value: 'SC', name: 'South Carolina' },
    { value: 'SD', name: 'South Dakota' },
    { value: 'TN', name: 'Tennessee' },
    { value: 'TX', name: 'Texas' },
    { value: 'UT', name: 'Utah' },
    { value: 'VT', name: 'Vermont' },
    { value: 'VA', name: 'Virginia' },
    { value: 'WA', name: 'Washington' },
    { value: 'WV', name: 'West Virginia' },
    { value: 'WI', name: 'Wisconsin' },
    { value: 'WY', name: 'Wyoming' },
    { value: 'FM', name: 'Micronesia' },
    { value: 'GU', name: 'Guam' },
    { value: 'MH', name: 'Marshall Islands' },
    { value: 'MP', name: 'Northern Mariana Islands' },
    { value: 'PW', name: 'Palau' },
    { value: 'PR', name: 'Puerto Rico' },
    { value: 'VI', name: 'Virgin Islands' },
    { value: 'AB', name: 'Alberta' },
    { value: 'BC', name: 'British Columbia' },
    { value: 'MB', name: 'Manitoba' },
    { value: 'NB', name: 'New Brunswick' },
    { value: 'NL', name: 'Newfoundland and Labrador' },
    { value: 'NS', name: 'Nova Scotia' },
    { value: 'NT', name: 'Northwest Territories' },
    { value: 'NU', name: 'Nunavut' },
    { value: 'ON', name: 'Ontario' },
    { value: 'PE', name: 'Prince Edward Island' },
    { value: 'QC', name: 'Quebec' },
    { value: 'SK', name: 'Saskatchewan' },
    { value: 'YT', name: 'Yukon' },
  ];

  resetForm() {
    this.searchForm = {};
    this.selectedStates = [];
    this.isFormValid = false;
    this.isAdvancedSearchVisible = false;
    this.isSearchTypeDropdownOpen = false;
    this.filterCount = 0;
    this.filterCountChanged.emit(0);
    this.reloadAllData.emit();
  }

  toggleAdvancedSearch() {
    this.isAdvancedSearchVisible = !this.isAdvancedSearchVisible;
  }

  onStateFilterChange(values: string[]) {
    this.selectedStates = values;
    this.searchForm.state = values;
    this.checkFormValidity();
  }

  isDateRangeInvalid(): boolean {
    if (
      !this.searchForm.opened_for_business_from ||
      !this.searchForm.opened_for_business_to
    ) {
      return false;
    }
    const fromDate = new Date(this.searchForm.opened_for_business_from);
    const toDate = new Date(this.searchForm.opened_for_business_to);
    return fromDate > toDate;
  }

  checkFormValidity() {
    // Check if any field has a value
    const hasValue = Object.entries(this.searchForm).some(
      ([, value]) =>
        value !== undefined &&
        value !== null &&
        value.toString().trim().length > 0,
    );

    // If either opened_for_business_from or opened_for_business_to is set, require both for validity
    const hasDateRange =
      (!this.searchForm.opened_for_business_from &&
        !this.searchForm.opened_for_business_to) ||
      (!!this.searchForm.opened_for_business_from &&
        !!this.searchForm.opened_for_business_to);

    // Check if date range is valid
    const isDateRangeValid = !this.isDateRangeInvalid();

    this.isFormValid = hasValue && hasDateRange && isDateRangeValid;
  }

  onSubmit() {
    if (!this.isFormValid) return;

    if (
      typeof this.searchForm.sic_codes === 'string' &&
      this.searchForm.sic_codes.trim() !== ''
    ) {
      this.searchForm.sic_codes = this.searchForm.sic_codes
        .split(',')
        .map((code) => code.trim())
        .filter(Boolean);
    }
    if (
      typeof this.searchForm.naics_codes === 'string' &&
      this.searchForm.naics_codes.trim() !== ''
    ) {
      this.searchForm.naics_codes = this.searchForm.naics_codes
        .split(',')
        .map((code) => code.trim())
        .filter(Boolean);
    }
    if (
      typeof this.searchForm.primary_sic_code === 'string' &&
      this.searchForm.primary_sic_code.trim() !== ''
    ) {
      this.searchForm.primary_sic_code = this.searchForm.primary_sic_code
        .split(',')
        .map((code) => code.trim())
        .filter(Boolean);
    }
    if (
      typeof this.searchForm.primary_naics_code === 'string' &&
      this.searchForm.primary_naics_code.trim() !== ''
    ) {
      this.searchForm.primary_naics_code = this.searchForm.primary_naics_code
        .split(',')
        .map((code) => code.trim())
        .filter(Boolean);
    }
    if (
      typeof this.searchForm.postal_code === 'string' &&
      this.searchForm.postal_code.trim() !== ''
    ) {
      this.searchForm.postal_code = this.searchForm.postal_code
        .split(',')
        .map((code) => code.trim())
        .filter(Boolean);
    }
    if (
      typeof this.searchForm.city === 'string' &&
      this.searchForm.city.trim() !== ''
    ) {
      this.searchForm.city = this.searchForm.city
        .split(',')
        .map((code) => code.trim())
        .filter(Boolean);
    }
    if (
      typeof this.searchForm.street === 'string' &&
      this.searchForm.street.trim() !== ''
    ) {
      this.searchForm.street = this.searchForm.street
        .split(',')
        .map((code) => code.trim())
        .filter(Boolean);
    }

    const filtersBuilder = new AdvancedFiltersBuilder();

    if (this.searchForm.phone_number) {
      filtersBuilder.advanced_filters.push({
        attribute: 'phone',
        relation: 'matches',
        value: this.searchForm.phone_number,
      });
    }

    if (this.searchForm.postal_code) {
      filtersBuilder.advanced_filters.push({
        attribute: 'postal_code',
        relation: 'in',
        value: this.searchForm.postal_code,
      });
    }
    if (this.searchForm.city) {
      filtersBuilder.advanced_filters.push({
        attribute: 'city',
        relation: 'in',
        value: this.searchForm.city,
      });
    }
    if (this.searchForm.street) {
      filtersBuilder.advanced_filters.push({
        attribute: 'street',
        relation: 'in',
        value: this.searchForm.street,
      });
    }

    if (this.searchForm.work_at_home) {
      filtersBuilder.advanced_filters.push({
        attribute: 'work_at_home',
        relation: 'equals',
        value: this.searchForm.work_at_home,
      });
    }

    if (this.searchForm.state && this.searchForm.state.length > 0) {
      filtersBuilder.advanced_filters.push({
        attribute: 'state',
        relation: 'in',
        value: Array.isArray(this.searchForm.state)
          ? this.searchForm.state
          : [this.searchForm.state],
      });
    }

    if (this.searchForm.primary_sic_code) {
      filtersBuilder.advanced_filters.push({
        attribute: 'primary_sic_code_id',
        relation: 'in',
        value: this.searchForm.primary_sic_code,
      });
    }

    if (this.searchForm.sic_codes) {
      filtersBuilder.advanced_filters.push({
        attribute: 'sic_code_ids',
        relation: 'in',
        value: this.searchForm.sic_codes,
      });
    }

    if (this.searchForm.primary_naics_code) {
      filtersBuilder.advanced_filters.push({
        attribute: 'primary_naics_code_id',
        relation: 'in',
        value: this.searchForm.primary_naics_code,
      });
    }

    if (this.searchForm.naics_codes) {
      filtersBuilder.advanced_filters.push({
        attribute: 'naics_code_ids',
        relation: 'in',
        value: this.searchForm.naics_codes,
      });
    }

    if (this.searchForm.location_employee_count) {
      filtersBuilder.advanced_filters.push({
        attribute: 'location_employee_count',
        relation: this.searchForm.location_employee_count_operator || 'equals',
        value: this.searchForm.location_employee_count,
      });
    }

    if (this.searchForm.fips_code) {
      filtersBuilder.advanced_filters.push({
        attribute: 'fips_code',
        relation: 'in',
        value: [this.searchForm.fips_code],
      });
    }

    if (this.searchForm.location_sales_volume) {
      filtersBuilder.advanced_filters.push({
        attribute: 'location_sales_volume',
        relation: this.searchForm.location_sales_volume_operator || 'equals',
        value: this.searchForm.location_sales_volume,
      });
    }

    if (
      this.searchForm.opened_for_business_from &&
      this.searchForm.opened_for_business_to
    ) {
      filtersBuilder.advanced_filters.push({
        attribute: 'opened_for_business_on',
        relation: 'between',
        value: [
          this.searchForm.opened_for_business_from,
          this.searchForm.opened_for_business_to,
        ],
      });
    }

    const filter: AdvancedFiltersBuilder = {
      connective: this.matchType === 'all' ? 'and' : 'or',
      advanced_filters: filtersBuilder.advanced_filters,
    };
    this.filterCount = filtersBuilder.advanced_filters.length;
    this.isAdvancedSearchVisible = false;
    this.filterCountChanged.emit(this.filterCount);
    this.searchSubmitted.emit(filter);
  }
}
